// Pure day-of-show helpers — no React, no I/O, so they unit-test deterministically. The run-of-show
// CLOCK is a 30s LOCAL render tick in VENUE-LOCAL time ("local tickers stay local", NEVER a WS event;
// 's venue-local basis governs the clock, not device-local). "Your Set" = the `stage` run-of-show item
// (the on-stage performance; sections are load_in|load_out|soundcheck|stage|vip|custom, 03 ShowScheduleItem).
import type { components } from "@cntrl/api-client";
import { parseWallClock, showNightMinute } from "../../app/showNight.js";
import { ownStageRow } from "../../app/showLineup.js";

type ScheduleItemDto = components["schemas"]["ScheduleItemDto"];
type ShowSummary = components["schemas"]["ShowSummary"];
type LiveActivityPlan = components["schemas"]["LiveActivityPlan"];

/** `HH:mm[:ss]` (a `time`-format wall-clock, no date) → minutes since midnight, or null if unparseable.
 *  Re-exported from the shared show-night module — this used to be the only copy, and the 6am rule beside it
 *  was the only copy too, which is how everything OUTSIDE this file read a 00:30 set as ~24h early. */
export const parseTimeToMinutes = parseWallClock;

/**
 * The current wall-clock minutes-since-midnight AT THE VENUE (its IANA `timezone`), so the clock compares the
 * run-of-show times (venue-local wall-clock) against venue-local now — not the device's. An absent/invalid tz
 * falls back to device-local time (still a sane clock; the server fix already skips bad-tz shows, so this
 * is defense-in-depth). `now` is injectable for deterministic tests.
 */
export function venueNowMinutes(timezone: string | null | undefined, now: Date = new Date()): number {
  if (timezone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }).formatToParts(now);
      const h = Number(parts.find((p) => p.type === "hour")?.value);
      const min = Number(parts.find((p) => p.type === "minute")?.value);
      if (Number.isFinite(h) && Number.isFinite(min)) return (h % 24) * 60 + min;
    } catch {
      // invalid IANA zone → device-local fallback below
    }
  }
  return now.getHours() * 60 + now.getMinutes();
}

export type SetStatus = "before" | "now" | "done" | "none";

export interface RunOfShowPhases {
  /** The run-of-show item in progress now (the latest one whose start ≤ now), or null before the first item. */
  current: ScheduleItemDto | null;
  /** The next item to start (the earliest whose start > now), or null once everything has started. */
  next: ScheduleItemDto | null;
  /** The band's OWN on-stage set — the `stage` row attributed to our lineup slot — or null. */
  yourSet: ScheduleItemDto | null;
  setStatus: SetStatus;
  /** Whole minutes until the set starts (≥0), or null when it has started / there is no timed set. */
  minutesToSet: number | null;
}

/** Default set length (minutes) when a `stage` row carries no `durationMin` — used only to decide now-vs-done. */
const DEFAULT_SET_MINUTES = 60;

/**
 * The show-night day boundary now lives in one place ({@link showNightMinute}) because it is not a
 * day-of-show concern — the editor, the server-side ordering and the reminder sweep all need the same answer
 * to "which day does 00:30 belong to?", and for a while only this file had it.
 *
 * Anchoring both the schedule AND `now` to this axis is what makes a 00:30 set read "on stage now" at 00:35
 * rather than "Set complete" all evening, and keeps Now/Up Next chronologically ordered across midnight.
 */
const unwrap = showNightMinute;

/**
 * Resolve Now / Up Next / Your Set from the run of show at a given venue-local minute. PURE — the caller feeds
 * {@link venueNowMinutes}. Times are placed on a 6am-anchored monotonic axis ({@link unwrap}) so a set that
 * starts after midnight sorts and compares correctly against an evening `now`. Items without a parseable
 * `startsAt` are ignored for Now/Up-Next ordering (an untimed row can't sit on the timeline) but a `stage` row is
 * still surfaced as the set even if untimed.
 */
export function selectRunOfShowPhases(
  items: readonly ScheduleItemDto[],
  nowMinutes: number,
  /** OUR act's lineup slot — the band's own set is the `stage` row carrying it. See {@link ownStageRow}. */
  ourSlotId: string | null = null,
): RunOfShowPhases {
  const now = unwrap(nowMinutes);
  const timed = items
    .map((i) => {
      const raw = parseTimeToMinutes(i.startsAt);
      return raw == null ? null : { i, m: unwrap(raw) };
    })
    .filter((x): x is { i: ScheduleItemDto; m: number } => x != null)
    .sort((a, b) => a.m - b.m);

  let current: ScheduleItemDto | null = null;
  let next: ScheduleItemDto | null = null;
  for (const { i, m } of timed) {
    if (m <= now) current = i;
    else {
      next = i;
      break;
    }
  }

  // NOT "the first stage row" — four rows carry that section since the structured rebuild, so the first one
  // is usually a changeover (untimed ⇒ the whole set clock silently gave up) or the support act's set.
  const yourSet = ownStageRow(items, ourSlotId);
  let setStatus: SetStatus = "none";
  let minutesToSet: number | null = null;
  if (yourSet) {
    const raw = parseTimeToMinutes(yourSet.startsAt);
    if (raw == null) {
      setStatus = "none"; // a set with no time can't be placed on the clock
    } else {
      const setMin = unwrap(raw);
      if (now < setMin) {
        setStatus = "before";
        minutesToSet = setMin - now;
      } else {
        const end = setMin + (yourSet.durationMin ?? DEFAULT_SET_MINUTES);
        setStatus = now < end ? "now" : "done";
        minutesToSet = 0;
      }
    }
  }

  return { current, next, yourSet, setStatus, minutesToSet };
}

/** A run-of-show label for display: the row's own label, else its humanised section, else "Item". */
export function runOfShowLabel(item: ScheduleItemDto): string {
  return item.label?.trim() || (item.section ?? "").replace(/_/g, " ").trim() || "Item";
}

/** Default phase length (minutes) by section, when a row carries no `durationMin`. `doors` (and any instant)
 *  is 0 — an instant reads as live for 30 min but has no duration to render ("—"). */
function sectionDefaultMinutes(section: string | null | undefined): number {
  switch (section) {
    case "doors":
      return 0;
    case "soundcheck":
      return 20;
    case "stage":
      return 60;
    case "load_out":
      return 90;
    default:
      return 30;
  }
}

/** The effective duration of a row (its own `durationMin`, else the section default). */
export function rowDurationMinutes(item: ScheduleItemDto): number {
  return item.durationMin ?? sectionDefaultMinutes(item.section);
}

/** How long an INSTANT row (doors, dur 0) stays "live" after its start — spec: 30 minutes. */
const INSTANT_LIVE_MINUTES = 30;

export type RunRowStatus = "past" | "live" | "future";

export interface RunRow {
  item: ScheduleItemDto;
  status: RunRowStatus;
  /** Our band's own on-stage set row. */
  mine: boolean;
  /** A soundcheck row — carries the outlined SOUNDCHECK chip so a 2:30 soundcheck can't read like a 9:00 set. */
  isSoundcheck: boolean;
  /** A load-in row — carries the outlined LOAD IN chip, the soundcheck chip's sibling (Ryan, ART-174): an
   *  act's load-in row is otherwise indistinguishable from its set. */
  isLoadIn: boolean;
  /** "45 min", or "—" for an instant (doors) / untimed row. */
  durationLabel: string;
}

/**
 * Resolve each run-of-show row's live state for the timeline list. Exactly ONE row is "live" — the earliest
 * whose window contains `now` (overlaps resolve to the earliest, matching the Now card). Everything earlier is
 * "past", everything later "future"; an untimed row is "future" (it can't be placed). PURE — the caller feeds
 * {@link venueNowMinutes}. Rows are returned in the given display order.
 */
export function runOfShowRows(
  items: readonly ScheduleItemDto[],
  nowMinutes: number,
  ourSlotId: string | null = null,
): RunRow[] {
  const now = unwrap(nowMinutes);
  // The single live row: earliest whose [start, end) contains now.
  let liveKey: ScheduleItemDto | null = null;
  let liveStart = Infinity;
  for (const i of items) {
    const raw = parseTimeToMinutes(i.startsAt);
    if (raw == null) continue;
    const start = unwrap(raw);
    const dur = rowDurationMinutes(i);
    const end = start + (dur > 0 ? dur : INSTANT_LIVE_MINUTES);
    if (now >= start && now < end && start < liveStart) {
      liveStart = start;
      liveKey = i;
    }
  }
  return items.map((item) => {
    const raw = parseTimeToMinutes(item.startsAt);
    const dur = rowDurationMinutes(item);
    let status: RunRowStatus = "future";
    if (raw != null) {
      const start = unwrap(raw);
      const end = start + (dur > 0 ? dur : INSTANT_LIVE_MINUTES);
      status = item === liveKey ? "live" : end <= now ? "past" : "future";
    }
    return {
      item,
      status,
      mine: item.section === "stage" && item.slotId != null && item.slotId === ourSlotId,
      isSoundcheck: item.section === "soundcheck",
      isLoadIn: item.section === "load_in",
      durationLabel: dur > 0 ? `${dur} min` : "—",
    };
  });
}

export type NowCardMode = "pre" | "live" | "wrapped";

export interface NowCardState {
  mode: NowCardMode;
  /** The accent eyebrow — "ON STAGE NOW" / "NEXT IN 2H 15M" / "WRAPPED". */
  eyebrow: string;
  /** The right-hand counter chip — "LIVE" / the next start time / "". */
  counter: string;
  /** The big display line — the act/phase name, or "ALL WRAPPED". */
  title: string;
  /** The time window sub-line ("9:30 PM–10:15 PM · 45 min"), or a closing line when wrapped. */
  sub: string;
  /** 0..1 fill for the live progress bar, or null (pre-show / wrapped / instant). */
  progress: number | null;
  /** "12m in" / null. */
  elapsed: string | null;
  /** "33m left" / null. */
  remaining: string | null;
}

/** An uppercase compact countdown ("2H 15M" / "45M") for the pre-show eyebrow. */
function compactCountdown(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min}M`;
  if (min === 0) return `${h}H`;
  return `${h}H ${min}M`;
}

/** Whole days between two `yyyy-mm-dd` dates — positive when the show is in the FUTURE, 0 today, negative
 *  past. Parsed as LOCAL calendar fields (never `toISOString()`), the {@link isoDateOffset} rule. */
export function showDayDelta(dateIso: string | null | undefined, todayIso: string): number {
  if (!dateIso) return 0;
  const [ty, tm, td] = todayIso.split("-").map(Number);
  const [dy, dm, dd] = dateIso.split("-").map(Number);
  return Math.round((new Date(dy, dm - 1, dd).getTime() - new Date(ty, tm - 1, td).getTime()) / 86_400_000);
}

/** The future Now-hero headline — "Show tomorrow" or "Show in N days". Only meaningful for `dayDelta > 0`. */
export function futureShowHeadline(dayDelta: number): string {
  return dayDelta === 1 ? "Show tomorrow" : `Show in ${dayDelta} days`;
}

/**
 * The Now card's state — Pre-show (counting to the next row), Live (the current phase, with a progress bar),
 * or Wrapped (after the last row). Returns null when there is no timed row at all, so the caller can render the
 * "add doors + set time on desktop" hint instead. PURE — the caller feeds {@link venueNowMinutes}.
 *
 * DATE-AWARE via `dayDelta` (0 today, negative past): the run of show carries wall-clock TIMES with no date,
 * so the live/pre/wrapped logic is only meaningful ON the show day. A past show is over — it wraps outright,
 * rather than reading the current time-of-day against last month's set times (which showed "wrapped", or a
 * bogus morning "pre", on a show that already happened). A FUTURE show never reaches here — the caller renders
 * the day-countdown hero instead (Ryan, ART-174: "all wrapped for a show that hasn't started makes no sense").
 */
export function selectNowCard(
  items: readonly ScheduleItemDto[],
  nowMinutes: number,
  dayDelta = 0,
): NowCardState | null {
  const now = unwrap(nowMinutes);
  // A past show is done, whatever the wall clock reads today.
  if (dayDelta < 0) {
    const anyTimed = items.some((i) => parseTimeToMinutes(i.startsAt) != null);
    return anyTimed ? wrappedCard() : null;
  }
  const timed = items
    .map((i) => {
      const raw = parseTimeToMinutes(i.startsAt);
      return raw == null ? null : { i, start: unwrap(raw), dur: rowDurationMinutes(i) };
    })
    .filter((x): x is { i: ScheduleItemDto; start: number; dur: number } => x != null)
    .sort((a, b) => a.start - b.start);

  if (timed.length === 0) return null;

  const windowOf = (e: { i: ScheduleItemDto; start: number; dur: number }): string =>
    e.dur > 0 ? `${format12h(e.start)}–${format12h(e.start + e.dur)} · ${e.dur} min` : format12h(e.start);

  const live = timed.find((e) => {
    const end = e.start + (e.dur > 0 ? e.dur : INSTANT_LIVE_MINUTES);
    return now >= e.start && now < end;
  });
  if (live) {
    const isSet = live.i.section === "stage";
    const spent = now - live.start;
    const hasBar = live.dur > 0;
    return {
      mode: "live",
      eyebrow: isSet ? "ON STAGE NOW" : "LIVE NOW",
      counter: "LIVE",
      title: runOfShowLabel(live.i),
      sub: windowOf(live),
      progress: hasBar ? Math.min(1, Math.max(0, spent / live.dur)) : null,
      elapsed: hasBar ? `${Math.max(0, Math.round(spent))}m in` : null,
      remaining: hasBar ? `${Math.max(0, Math.round(live.dur - spent))}m left` : null,
    };
  }

  const next = timed.find((e) => e.start > now);
  if (next) {
    return {
      mode: "pre",
      eyebrow: `NEXT IN ${compactCountdown(next.start - now)}`,
      counter: format12h(next.start),
      title: runOfShowLabel(next.i),
      sub: windowOf(next),
      progress: null,
      elapsed: null,
      remaining: null,
    };
  }

  return wrappedCard();
}

/** The Wrapped Now-card — the show is over. No glow, no dot. Shared by the past-show and end-of-day paths. */
function wrappedCard(): NowCardState {
  return {
    mode: "wrapped",
    eyebrow: "WRAPPED",
    counter: "",
    title: "All wrapped",
    sub: "Show complete — load-out done, drive safe.",
    progress: null,
    elapsed: null,
    remaining: null,
  };
}

/** 12-hour local ("8:00 PM") from minutes-since-midnight (wrapped, so the post-midnight tail on the 6am axis
 *  renders correctly). The DOS spec is emphatic: 12-hour local, everywhere — "a rendered 19:30 is a bug". */
export function format12h(mins: number): string {
  const m = ((Math.round(mins) % 1440) + 1440) % 1440;
  const h24 = Math.floor(m / 60);
  const min = m % 60;
  const ampm = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(min).padStart(2, "0")} ${ampm}`;
}

/** 12-hour local ("8:00 PM") for a `time` value like `19:30:00` — timeline rows, doors, Your Set.
 *  Empty string if unparseable. */
export function formatClock(t: string | null | undefined): string {
  const m = parseTimeToMinutes(t);
  return m == null ? "" : format12h(m);
}

/** A friendly "in Xh Ym" / "in Ym" countdown for the set (minutes ≥ 0). */
export function formatCountdown(minutes: number): string {
  if (minutes <= 0) return "now";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `in ${m}m`;
  if (m === 0) return `in ${h}h`;
  return `in ${h}h ${m}m`;
}

/** A short "MMM d, h:mm a" for a full date-time (flight depart/arrive), in the item's own tz when known (else
 * device-local). Empty string on a null/invalid value. */
export function formatWhen(iso: string | null | undefined, tz?: string | null, now: number = Date.now()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Show the YEAR only when the instant is NOT in the current year (compared in the same zone). A same-year
  // date stays year-less as before, but a stale/past-year flight then reads as "Sep 19, 2025 · …" rather than
  // a bare "Sep 19" that looks upcoming next to a (correct) "Landed" badge — the ART-174 confusion. The phase
  // is right; the year-less label was the misleader.
  const yearIn = (ms: number): string => {
    try {
      return new Intl.DateTimeFormat("en-US", tz ? { timeZone: tz, year: "numeric" } : { year: "numeric" }).format(ms);
    } catch {
      return new Intl.DateTimeFormat("en-US", { year: "numeric" }).format(ms);
    }
  };
  const showYear = yearIn(d.getTime()) !== yearIn(now);
  const opts: Intl.DateTimeFormatOptions = {
    month: "short", day: "numeric", ...(showYear ? { year: "numeric" } : {}), hour: "numeric", minute: "2-digit",
  };
  try {
    return new Intl.DateTimeFormat("en-US", tz ? { ...opts, timeZone: tz } : opts).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", opts).format(d);
  }
}

/** `yyyy-mm-dd` for today (+`days` offset), **device-local** (a show ON today stays "current" until tomorrow).
 * Formats from LOCAL calendar fields, NOT `toISOString()` — `toISOString()` emits the UTC date, which in every
 * UTC-negative (Americas) zone rolls to tomorrow once local time passes UTC-midnight (evening), so the DOS tab
 * would drop tonight's show during the exact doors/soundcheck window (adversarial review). `now` is
 * injectable for tests. NOTE: BookingScreen's `isoOffset` carries the same latent `toISOString()` bug — a
 * pre-existing issue to fix separately (logged in).*/
export function isoDateOffset(days: number, now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** The list-card date label. Spec's four branches with two separators: TODAY · TOMORROW · a `≤7 days`
 *  self-built "SAT · AUG 2" (middot) · and beyond 7 days a single locale string "FRI, AUG 14" (comma) —
 *  the common case. `dateIso`/`todayIso` are `yyyy-mm-dd`. */
export function listCardDate(dateIso: string | null | undefined, todayIso: string): string {
  if (!dateIso) return "";
  if (dateIso === todayIso) return "TODAY";
  const [ty, tm, td] = todayIso.split("-").map(Number);
  const [dy, dm, dd] = dateIso.split("-").map(Number);
  const today = new Date(ty, tm - 1, td);
  const d = new Date(dy, dm - 1, dd);
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (days === 1) return "TOMORROW";
  const up = (s: string) => s.toUpperCase();
  if (days >= 2 && days <= 7) {
    const wd = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(d);
    const mo = new Intl.DateTimeFormat("en-US", { month: "short" }).format(d);
    return up(`${wd} · ${mo} ${dd}`);
  }
  return up(new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" }).format(d));
}

/** "Fri, Oct 9" for the DOS detail header title (`{venue} · {date}`). Empty string on a null/invalid date.
 *  Built from LOCAL calendar fields (never `new Date(iso)`, which parses a bare yyyy-mm-dd as UTC midnight and
 *  shifts a day west of Greenwich). */
export function formatShowDate(dateIso: string | null | undefined): string {
  if (!dateIso) return "";
  const [y, m, d] = dateIso.split("-").map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return "";
  return new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" })
    .format(new Date(y, m - 1, d));
}

/** Split a confirmed-shows list (already `from >= today`, sorted ascending) into today's shows + the rest.
 * Today = shows whose `date` equals `todayIso`. */
export function partitionByToday(
  shows: readonly ShowSummary[],
  todayIso: string,
): { today: ShowSummary[]; upcoming: ShowSummary[] } {
  const today: ShowSummary[] = [];
  const upcoming: ShowSummary[] = [];
  for (const s of shows) {
    if (s.date === todayIso) today.push(s);
    else upcoming.push(s);
  }
  return { today, upcoming };
}

// The Apple Maps universal-link origin. Assembled from parts on purpose: the noHardcodedHosts lint
// bans http(s):// literals because API/WS BASES must come from injected `window.__ENV__` — but a third-party
// consumer-app deep link is not a backend base (there is, by design, no env-injected "maps base"). It opens
// Apple Maps on iOS and the web map elsewhere, so it stays a fixed universal link, not config.
const MAPS_ORIGIN = ["https:", "//maps.apple.com"].join("");

/** An Apple Maps universal URL for tap-to-maps. Prefers a lat,lng pin when present (exact), else the free-text
 * address/venue query. Returns null when nothing locates (so the caller disables the tap target). */
export function mapsUrl(opts: {
  lat?: number | null;
  lng?: number | null;
  query?: string | null;
}): string | null {
  if (opts.lat != null && opts.lng != null) {
    return `${MAPS_ORIGIN}/?ll=${opts.lat},${opts.lng}`;
  }
  const q = opts.query?.trim();
  if (q) return `${MAPS_ORIGIN}/?q=${encodeURIComponent(q)}`;
  return null;
}

/** A one-line location string for a show/venue (venue · city, state) — for headers + maps queries. */
export function locationLine(
  parts: { venueName?: string | null; city?: string | null; state?: string | null; country?: string | null },
): string {
  return [parts.venueName, parts.city, parts.state]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(" · ");
}

// The consumer ride-app universal-link origins — same rationale as MAPS_ORIGIN (a third-party consumer app is
// not a backend base, so it stays a fixed universal link assembled from parts to satisfy the noHardcodedHosts
// lint). Both open the native app when installed and the web flow otherwise. Uber's `/looking` flow is the
// current one — the legacy `/ul/?action=setPickup&dropoff[...]` links no longer prefill the dropoff on desktop
// web (they open Uber with an empty destination), which is what ART-174 hit.
const UBER_ORIGIN = ["https:", "//m.uber.com/looking"].join("");
const LYFT_ORIGIN = ["https:", "//lyft.com/ride"].join("");

/** A tappable destination — a venue or a hotel — for the maps / rideshare / copy actions. `lat`/`lng` are the
 *  exact pin when we hold them (venue); a hotel carries only its address, which every consumer geocodes. */
export interface PlaceRef {
  name?: string | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  /** A hotel's booking confirmation code — offered as its own Copy action in the place sheet. Absent for a venue. */
  confirmationCode?: string | null;
}

/** The human-readable "name, address" line for a place — what Copy puts on the clipboard and what the maps /
 *  Uber address geocode fall back to when there are no coordinates. Empty when the place has neither. */
export function placeQuery(p: PlaceRef): string {
  return [p.name, p.address].map((s) => s?.trim()).filter(Boolean).join(", ");
}

/** Apple Maps link for a place — the lat/lng pin when present (exact), else the free-text query. Null when the
 *  place locates to nothing (so the caller withholds the "Open Address" action rather than opening a blank map). */
export function placeMapsUrl(p: PlaceRef): string | null {
  return mapsUrl({ lat: p.lat, lng: p.lng, query: placeQuery(p) });
}

// Keys carry literal brackets (Lyft's documented deep-link shape); only VALUES are percent-encoded.
function rideQuery(pairs: readonly (readonly [string, string])[]): string {
  return pairs.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
}

/** Uber deep link to drop off at a place, via the current `/looking` flow (the legacy `/ul` setPickup links no
 *  longer prefill on desktop web). The dropoff prefills from the COORDINATES in the `drop[0]` object — an
 *  address alone does not set a destination on the web flow — so a coordinate-less place (a hotel with no
 *  geocode yet) opens Uber carrying the address but no pin. Null only when there is nothing to pass at all. */
export function uberRideUrl(p: PlaceRef): string | null {
  const q = placeQuery(p);
  if (p.lat == null && !q) return null;
  const drop: Record<string, number | string> = {};
  if (p.lat != null && p.lng != null) {
    drop.latitude = p.lat;
    drop.longitude = p.lng;
  }
  if (p.name?.trim()) drop.addressLine1 = p.name.trim();
  if (q) drop.addressLine2 = q;
  return `${UBER_ORIGIN}?drop[0]=${encodeURIComponent(JSON.stringify(drop))}`;
}

/** Lyft deep link to a place. Lyft's deep link accepts destination COORDINATES only (no address geocode), so
 *  without lat/lng it opens Lyft with no preset destination rather than routing to the wrong one. Hotels gain
 *  coordinates at travel-save time (geocoded server-side); until then a coordinate-less place opens Lyft bare. */
export function lyftRideUrl(p: PlaceRef): string {
  const pairs: [string, string][] = [["id", "lyft"]];
  if (p.lat != null && p.lng != null) {
    pairs.push(["destination[latitude]", String(p.lat)], ["destination[longitude]", String(p.lng)]);
  }
  return `${LYFT_ORIGIN}?${rideQuery(pairs)}`;
}

export type FlightPhase = "pre" | "flying" | "landed";

export interface FlightProgress {
  phase: FlightPhase;
  /** 0..1 position of the plane along the leg by wall clock (0 before departure, 1 after arrival). */
  fraction: number;
}

/**
 * The live position of a flight along its leg, by wall clock — for the logistics flight strip's fill bar +
 * plane. PURE (inject `now` for tests). Before departure → pre/0; after arrival → landed/1; in between →
 * flying with the elapsed fraction. Unparseable/degenerate times fall back to pre/0 (never a NaN width).
 */
export function flightProgress(
  departIso: string | null | undefined,
  arriveIso: string | null | undefined,
  now: number = Date.now(),
): FlightProgress {
  const dep = departIso ? new Date(departIso).getTime() : NaN;
  const arr = arriveIso ? new Date(arriveIso).getTime() : NaN;
  if (!Number.isFinite(dep) || !Number.isFinite(arr) || arr <= dep) {
    return { phase: Number.isFinite(dep) && now >= dep ? "landed" : "pre", fraction: 0 };
  }
  if (now < dep) return { phase: "pre", fraction: 0 };
  if (now >= arr) return { phase: "landed", fraction: 1 };
  return { phase: "flying", fraction: (now - dep) / (arr - dep) };
}

export interface JourneyLeg {
  departIso?: string | null;
  arriveIso?: string | null;
}

export interface JourneyProgress {
  /** `layover` = parked on a connection node between two legs. */
  phase: "pre" | "flying" | "layover" | "landed";
  /** 0..1 position of the plane across the WHOLE stitched journey (legs laid out as equal segments). */
  fraction: number;
}

/**
 * The plane's position across a MULTI-LEG journey by wall clock — for the logistics flight strip stitched into
 * one bar. Legs are laid out as equal segments (0..1); the plane advances within the active leg and PARKS on
 * the connection node during a layover. PURE (inject `now`). A single leg is just the two-endpoint case.
 */
export function journeyProgress(legs: readonly JourneyLeg[], now: number = Date.now()): JourneyProgress {
  const n = legs.length;
  if (n === 0) return { phase: "pre", fraction: 0 };
  const t = legs.map((l) => ({
    dep: l.departIso ? new Date(l.departIso).getTime() : NaN,
    arr: l.arriveIso ? new Date(l.arriveIso).getTime() : NaN,
  }));
  const first = t[0].dep;
  const last = t[n - 1].arr;
  if (!Number.isFinite(first) || now < first) return { phase: "pre", fraction: 0 };
  if (Number.isFinite(last) && now >= last) return { phase: "landed", fraction: 1 };
  for (let i = 0; i < n; i++) {
    const { dep, arr } = t[i];
    if (Number.isFinite(dep) && Number.isFinite(arr) && now >= dep && now < arr) {
      return { phase: "flying", fraction: (i + (now - dep) / (arr - dep)) / n };
    }
    if (i < n - 1 && Number.isFinite(arr) && now >= arr && now < t[i + 1].dep) {
      return { phase: "layover", fraction: (i + 1) / n };
    }
  }
  return { phase: "pre", fraction: 0 };
}

// ── DOS Live Activity plan ─────────────────────────────────────────────────────────────
//
// The device computes the plan because "the server runs in UTC and the schedule stores wall-clock strings with
// no timezone, so only the device can correctly resolve '8:00 PM' to an absolute instant" (the MVP rationale,
// preserved). This reuses the SAME 6am-anchored venue-local axis the clock uses ({@link unwrap}) so a
// post-midnight set lands on the correct calendar day.

/** The offset (ms, +east of UTC) that the IANA `tz` was at the given UTC instant. Uses Intl to read the local
 * wall-clock of that instant and diffs it against the instant — the standard no-library tz-offset probe. */
function tzOffsetMs(tz: string, utcMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs));
  const f = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asIfUtc = Date.UTC(f("year"), f("month") - 1, f("day"), f("hour") % 24, f("minute"), f("second"));
  return asIfUtc - utcMs;
}

/**
 * A venue-local wall-clock (the show's `dateIso` at `minutes` since local midnight, where `minutes` may exceed
 * 1440 for the post-midnight tail on the 6am axis) → an absolute epoch-MILLIS. Two-pass offset refinement so it
 * is correct across a DST boundary except the ~1h fall-back ambiguity (acceptable for a lock-screen glance;
 * exact segment hand-off is [future]). `tz` absent/invalid → device-local (the {@link venueNowMinutes}
 * fallback posture).
 */
export function venueWallClockToEpochMs(dateIso: string, minutes: number, tz: string | null | undefined): number {
  const [y, mo, d] = dateIso.split("-").map(Number);
  const dayOffset = Math.floor(minutes / 1440);
  const mins = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(mins / 60);
  const min = mins % 60;
  // Device-local fallback (the local Date constructor), NOT Date.UTC — so an absent/invalid tz matches the
  // device-local posture venueNowMinutes uses (the in-app clock reads that; the two must agree, and in the
  // common case where the phone is IN the venue's zone the local instant is the correct one). Date.UTC is only
  // the offset-probe intermediate on the tz-present path (adversarial review).
  const local = new Date(y, mo - 1, d + dayOffset, h, min, 0).getTime();
  if (!tz) return local;
  const guessUtc = Date.UTC(y, mo - 1, d + dayOffset, h, min, 0);
  try {
    let utc = guessUtc - tzOffsetMs(tz, guessUtc);
    utc = guessUtc - tzOffsetMs(tz, utc); // refine once (offset may differ across the guess)
    return utc;
  } catch {
    return local;
  }
}

/** How far ahead of the first run-of-show item the push-to-start fires — two hours, so the card is up well before load-in.*/
const START_LEAD_MS = 2 * 60 * 60 * 1000;
/** ContentState staleness horizon ("stale-date ~1800s").*/
const STALE_MS = 30 * 60 * 1000;

/** A stable per-phase key so a re-registration's due-jobs dedupe. Prefers the row id; falls back to
 * section+index (still stable for an unchanged run of show). */
function phaseKey(item: ScheduleItemDto, index: number): string {
  return item.itemId ?? `${item.section ?? "item"}-${index}`;
}

/**
 * The lock-screen card's state (ART-208) — the native widget's `ContentState`, field for field. Every value is a
 * FINISHED display string or a flag: the widget only draws, so the device that resolved the times also does all the
 * formatting, once, and the server forwards these verbatim. Dates are epoch SECONDS (Apple's unit).
 */
export interface LiveActivityContentState {
  eyebrow: string;
  phaseLabel: string;
  /** The compact Dynamic Island's LEFT label (beside the mark): before doors, YOUR band's next item ("SOUNDCHECK");
   *  from doors on, what is happening now ("DOORS", or the act on stage). */
  compactLead: string;
  /** The compact island's RIGHT side when it is a clock time — the start of the item named on the left ("3:15 PM"). */
  compactTime?: string;
  /** …or, from doors on, a native COUNTDOWN to the end of the current segment (epoch seconds), drawn "-12:41". */
  compactCountdownTo?: number;
  timeWindow: string;
  nextLabel: string;
  nextValue: string;
  venue: string;
  isPreShow: boolean;
  isFinal: boolean;
  isInstant: boolean;
  phaseStart?: number;
  phaseEnd?: number;
  countdownTo?: number;
}

/** The loud label for a phase: the act's name for a set (uppercased) — the last set too: it used to read "FINAL SET",
 *  and the PO ruled every set is named by its band, in the big text and in UP NEXT alike. Load-in / load-out by their
 *  section names; anything else its own label uppercased. */
function phaseLabelFor(item: ScheduleItemDto, own?: { slotId: string | null; name: string }): string {
  if (item.section === "stage") {
    if (item.label?.trim()) return item.label.trim().toUpperCase();
    // An unlabelled set: the band's own (its slot, or no act at all) takes the band's name; another act's is "SET".
    if (own?.name && (item.slotId == null || item.slotId === own.slotId)) return own.name.toUpperCase();
    return "SET";
  }
  if (item.section === "load_in") return "LOAD IN";
  if (item.section === "load_out") return "LOAD OUT";
  return runOfShowLabel(item).toUpperCase();
}

/** The name a phase carries on the compact island's left: the ACT for a set, DOORS for doors, else the phase's own
 *  label. */
function compactNameFor(item: ScheduleItemDto, own?: { slotId: string | null; name: string }): string {
  if (item.section === "doors") return "DOORS";
  return phaseLabelFor(item, own);
}

/**
 * Build the device-computed Live-Activity plan from the loaded run of show (ART-208). Resolves each timed row to an
 * absolute epoch on the venue-local 6am axis, schedules a push-to-start at T-2h, a phase `update` at each boundary,
 * and an `end` when the final set ends.
 *
 * Lifecycle rules (the handoff): the phases run up to and including the last set — load-out is deliberately NOT a
 * phase (the card disappearing is the load-out signal) and survives only as the final set's AFTER hint. **No set
 * anywhere ⇒ no activity** (null), because a card with no set has nothing to end on.
 */
export function buildLiveActivityPlan(input: {
  timezone: string | null | undefined;
  dateIso: string;
  items: readonly ScheduleItemDto[];
  /** `bandId` rides the card so a tap on it can open THAT band's Day of Show (switching band first). */
  attributes: { showId: string; bandLabel: string; venue: string; city: string; bandId?: string };
  /** The band's OWN slot on the bill (ART-284) — see the pre-doors filter below. */
  ownSlotId?: string | null;
}): LiveActivityPlan | null {
  const { timezone, dateIso, items, attributes } = input;

  const sorted = items
    .map((i, index) => {
      const raw = parseTimeToMinutes(i.startsAt);
      return raw == null ? null : { i, index, m: unwrap(raw) };
    })
    .filter((x): x is { i: ScheduleItemDto; index: number; m: number } => x != null)
    .sort((a, b) => a.m - b.m);

  // Before doors the card is the band's OWN day — its load-in, its soundcheck — and everyone joins at doors (PO,
  // ART-284): another act's soundcheck at 4pm is not a phase of yours. A row attributed to another act's slot is
  // dropped until doors (with no doors row, until the first set); an unattributed row is kept, the `ownStageRow`
  // rule — never another act's row, but never lose a row nobody placed.
  const doorsAt = sorted.find((t) => t.i.section === "doors")?.m ?? sorted.find((t) => t.i.section === "stage")?.m;
  const ownSlot = input.ownSlotId ?? null;
  const own = { slotId: ownSlot, name: attributes.bandLabel };
  const all = sorted.filter((t) => doorsAt == null || t.m >= doorsAt || t.i.slotId == null || t.i.slotId === ownSlot);

  const lastSet = all.map((t) => t.i.section).lastIndexOf("stage");
  if (lastSet < 0) return null;   // the hard gate: no set, no card
  const loadOut = all.find((t, k) => k > lastSet && t.i.section === "load_out")
    ?? all.find((t) => t.i.section === "load_out");
  const timed = all.slice(0, lastSet + 1).filter((t) => t.i.section !== "load_out");

  const at = (t: { m: number }) => venueWallClockToEpochMs(dateIso, t.m, timezone);
  const boundaries = timed.map(at);
  const finalIdx = timed.length - 1;
  const startEpochMs = boundaries[0] - START_LEAD_MS;
  const endEpochMs = boundaries[finalIdx] + rowDurationMinutes(timed[finalIdx].i) * 60 * 1000;
  const venue = [attributes.venue, attributes.city].filter(Boolean).join(" · ");
  const sec = (ms: number) => Math.round(ms / 1000);

  const windowOf = (idx: number): string => {
    const dur = rowDurationMinutes(timed[idx].i);
    return dur > 0 ? `${format12h(timed[idx].m)} – ${format12h(timed[idx].m + dur)}` : format12h(timed[idx].m);
  };
  const glance = (idx: number): string => `${phaseLabelFor(timed[idx].i, own)} · ${format12h(timed[idx].m)}`;

  // The compact island (PO, ART-208). Up to doors it is the band's NEXT item and when it starts; from doors on it is
  // what is on now and a countdown to the end of it. With no doors row, the show starts at the first set.
  const doorsIdx = timed.findIndex((t) => t.i.section === "doors");
  const showStartIdx = doorsIdx >= 0 ? doorsIdx : timed.findIndex((t) => t.i.section === "stage");
  /** When the segment starting at `idx` ends: its own duration, or — for an instant like doors — the next item. */
  const segmentEnd = (idx: number): number | null => {
    const dur = rowDurationMinutes(timed[idx].i);
    if (dur > 0) return boundaries[idx] + dur * 60 * 1000;
    return idx < finalIdx ? boundaries[idx + 1] : null;
  };
  const upNext = (idx: number) => ({ compactLead: compactNameFor(timed[idx].i, own), compactTime: format12h(timed[idx].m) });
  const compactFor = (idx: number) => {
    if (idx < showStartIdx) return upNext(idx + 1);
    const end = segmentEnd(idx);
    return {
      compactLead: compactNameFor(timed[idx].i, own),
      ...(end != null ? { compactCountdownTo: sec(end) } : { compactTime: format12h(timed[idx].m) }),
    };
  };

  const contentState = (idx: number): LiveActivityContentState => {
    const item = timed[idx].i;
    const isFinal = idx === finalIdx;
    const isInstant = rowDurationMinutes(item) === 0;
    const start = boundaries[idx];
    return {
      eyebrow: "NOW",
      phaseLabel: phaseLabelFor(item, own),
      ...compactFor(idx),
      timeWindow: windowOf(idx),
      nextLabel: isFinal ? "AFTER" : "UP NEXT",
      nextValue: isFinal
        ? (loadOut ? `LOAD OUT · ${format12h(loadOut.m)}` : "LOAD OUT")
        : glance(idx + 1),
      venue,
      isPreShow: false,
      isFinal,
      isInstant,
      ...(isInstant ? {} : { phaseStart: sec(start), phaseEnd: sec(start + rowDurationMinutes(item) * 60 * 1000) }),
    };
  };

  // A state carrying a countdown goes stale the instant the countdown reaches zero: iOS redraws a Live Activity when
  // its stale date passes, and that redraw is the only thing that takes a finished "-0:00" off the island when the
  // next push is late or lost. Anything else keeps the ordinary horizon.
  const staleOf = (cs: LiveActivityContentState, boundaryMs: number): number =>
    cs.compactCountdownTo != null && cs.compactCountdownTo * 1000 > boundaryMs
      ? cs.compactCountdownTo * 1000
      : boundaryMs + STALE_MS;

  const phases = timed.flatMap((t, idx) => {
    const cs = contentState(idx);
    const phase = {
      key: phaseKey(t.i, t.index),
      boundaryEpochMs: boundaries[idx],
      staleEpochMs: staleOf(cs, boundaries[idx]),
      contentState: cs,
    };
    // A CHANGEOVER (a gap between this segment's end and the next item, from doors on) gets a boundary of its own, so
    // the island moves to the next band and their start time instead of sitting on a finished countdown. The card
    // itself is unchanged — only the compact fields move.
    const end = idx >= showStartIdx && idx < finalIdx && rowDurationMinutes(t.i) > 0 ? segmentEnd(idx) : null;
    if (end == null || end >= boundaries[idx + 1]) return [phase];
    const card: LiveActivityContentState = { ...phase.contentState };
    delete card.compactCountdownTo;
    return [
      phase,
      {
        key: `${phase.key}-gap`,
        boundaryEpochMs: end,
        staleEpochMs: end + STALE_MS,
        contentState: { ...card, ...upNext(idx + 1) },
      },
    ];
  });

  // The push-to-start state, live from T-2h to the first phase: the first phase as the headline, with a native
  // countdown to it (`countdownTo`) — the static eyebrow is only the fallback for a widget that cannot run one.
  const startContentState: LiveActivityContentState = {
    eyebrow: `NEXT IN ${compactCountdown(START_LEAD_MS / 60000)}`,
    phaseLabel: phaseLabelFor(timed[0].i, own),
    ...upNext(0),
    timeWindow: windowOf(0),
    nextLabel: "UP NEXT",
    nextValue: timed.length > 1 ? glance(1) : "",
    venue,
    isPreShow: true,
    isFinal: false,
    isInstant: false,
    countdownTo: sec(boundaries[0]),
  };

  return {
    timezone: timezone ?? null,
    startEpochMs,
    endEpochMs,
    staleAtStartMs: startEpochMs + STALE_MS,
    startContentState,
    attributes: {
      showId: attributes.showId,
      bandLabel: attributes.bandLabel,
      ...(attributes.bandId ? { bandId: attributes.bandId } : {}),
    },
    attributesType: "DosLiveActivityAttributes",
    phases,
  };
}

/**
 * The card state live at `nowMs` (ART-208): the last phase whose boundary has passed, else the pre-show state. What
 * a LOCAL start opens on — a card started by hand mid-show must show the set that is on, not "pre-show". The server's
 * hand-off picks the same way (`DosLaSendWorker.CurrentContentState`).
 */
export function liveActivityStateAt(plan: LiveActivityPlan, nowMs: number = Date.now()): unknown {
  let current: unknown = plan.startContentState;
  for (const p of plan.phases ?? []) if ((p.boundaryEpochMs ?? Infinity) <= nowMs) current = p.contentState;
  return current;
}

/** The stale date (epoch ms) of the state live at `nowMs` — what a LOCAL start passes, so a card started by hand goes
 *  stale (and redraws) at the same instant a pushed one would. */
export function liveActivityStaleAt(plan: LiveActivityPlan, nowMs: number = Date.now()): number {
  let stale = plan.staleAtStartMs ?? nowMs + STALE_MS;
  for (const p of plan.phases ?? []) if ((p.boundaryEpochMs ?? Infinity) <= nowMs) stale = p.staleEpochMs ?? nowMs + STALE_MS;
  return stale;
}
